import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApiKeyService } from "../services/api";
import { useNotification } from "../hooks/useNotification";
import { useConfirm } from "../components/ui/ConfirmProvider";
import { AiOutlineKey, AiOutlineDelete, AiOutlinePlus, AiOutlineCopy, AiOutlineCalendar, AiOutlineClockCircle, AiOutlineClose } from "react-icons/ai";
import { formatDateTime } from "../utils/dateUtils";

const MyApiKeys = () => {
    const navigate = useNavigate();
    const apiKeyService = useApiKeyService();
    const notify = useNotification();
    const confirm = useConfirm();
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newKeyData, setNewKeyData] = useState(null);
    const [description, setDescription] = useState("");
    const [expirationDays, setExpirationDays] = useState(30);

    useEffect(() => {
        loadKeys();
    }, []);

    const loadKeys = async () => {
        setLoading(true);
        try {
            const response = await apiKeyService.listMyApiKeys();
            setKeys(response.data);
        } catch (error) {
            notify.error("Error al cargar las API Keys");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateKey = async (e) => {
        e.preventDefault();
        try {
            const response = await apiKeyService.createApiKey({
                description,
                expirationDays: expirationDays === 0 ? null : expirationDays
            });
            setNewKeyData(response.data);
            notify.success("API Key generada exitosamente");
            loadKeys();
        } catch (error) {
            notify.error("Error al generar la API Key");
        }
    };

    const handleRevokeKey = async (id) => {
        const ok = await confirm({
            title: "Revocar API Key",
            message: "¿Estás seguro de revocar esta API Key? Esta acción no se puede deshacer.",
            confirmLabel: "Revocar",
            destructive: true,
        });
        if (!ok) return;

        try {
            await apiKeyService.revokeApiKey(id);
            notify.success("API Key revocada");
            loadKeys();
        } catch (error) {
            notify.error("Error al revocar la API Key");
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        notify.success("Copiado al portapapeles");
    };

    const formatDate = (dateString) =>
        dateString ? formatDateTime(dateString) : "Nunca";

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.titleContainer}>
                    <AiOutlineKey style={styles.titleIcon} />
                    <h1 style={styles.title}>Mis API Keys</h1>
                </div>
                <div style={styles.headerActions}>
                    <button
                        style={styles.addButton}
                        onClick={() => {
                            setNewKeyData(null);
                            setDescription("");
                            setShowCreateModal(true);
                        }}
                    >
                        <AiOutlinePlus style={{ marginRight: '8px' }} />
                        Generar Nueva Clave
                    </button>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/')}
                    >
                        <AiOutlineClose style={styles.backIcon} />
                        Cerrar
                    </button>
                </div>
            </div>

            <p style={styles.description}>
                Las API Keys te permiten autenticarte en el sistema boveda-cli y otras herramientas automatizadas
                sin necesidad de usar tu cuenta personal de forma interactiva.
            </p>

            {loading ? (
                <div style={styles.loading}>Cargando...</div>
            ) : (
                <div style={styles.tableCard}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeadRow}>
                                <th style={styles.th}>Descripción</th>
                                <th style={styles.th}>Creada el</th>
                                <th style={styles.th}>Expira el</th>
                                <th style={styles.th}>Último uso</th>
                                <th style={styles.th}>Estado</th>
                                <th style={styles.th}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={styles.emptyCell}>No tienes API Keys generadas.</td>
                                </tr>
                            ) : (
                                keys.map(key => (
                                    <tr key={key.id} style={styles.tr}>
                                        <td style={styles.td}>
                                            <div style={styles.keyDescription}>{key.description}</div>
                                            <div style={styles.keyPreview}>{key.keyPreview}</div>
                                        </td>
                                        <td style={styles.td}>{formatDate(key.createdAt)}</td>
                                        <td style={styles.td}>{formatDate(key.expiresAt)}</td>
                                        <td style={styles.td}>{formatDate(key.lastUsedAt)}</td>
                                        <td style={styles.td}>
                                            <span style={key.revoked ? styles.revokedBadge : styles.activeBadge}>
                                                {key.revoked ? "Revocada" : "Activa"}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {!key.revoked && (
                                                <button
                                                    style={styles.revokeButton}
                                                    onClick={() => handleRevokeKey(key.id)}
                                                    title="Revocar clave"
                                                >
                                                    <AiOutlineDelete />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showCreateModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <h2>{newKeyData ? "API Key Generada" : "Generar Nueva API Key"}</h2>
                            <button style={styles.closeButton} onClick={() => setShowCreateModal(false)}>×</button>
                        </div>

                        {!newKeyData ? (
                            <form onSubmit={handleCreateKey} style={styles.form}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Descripción</label>
                                    <input
                                        type="text"
                                        placeholder="Ej: Mi computadora personal / Servidor Importador"
                                        style={styles.input}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        required
                                    />
                                    <span style={styles.hint}>Un nombre descriptivo para identificar para qué se usa esta clave.</span>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Expiración</label>
                                    <select
                                        style={styles.select}
                                        value={expirationDays}
                                        onChange={(e) => setExpirationDays(parseInt(e.target.value))}
                                    >
                                        <option value={7}>7 días</option>
                                        <option value={30}>30 días</option>
                                        <option value={90}>90 días</option>
                                        <option value={365}>1 año</option>
                                        <option value={0}>Sin expiración</option>
                                    </select>
                                </div>
                                <div style={styles.modalFooter}>
                                    <button type="button" style={styles.cancelButton} onClick={() => setShowCreateModal(false)}>Cancelar</button>
                                    <button type="submit" style={styles.submitButton}>Generar Clave</button>
                                </div>
                            </form>
                        ) : (
                            <div style={styles.successContent}>
                                <div style={styles.warningAlert}>
                                    <strong>⚠️ IMPORTANTE:</strong> Esta es la única vez que verás esta clave completa.
                                    Cópiala y guárdala en un lugar seguro ahora mismo. Si la pierdes, deberás generar una nueva.
                                </div>

                                <div style={styles.keyDisplay}>
                                    <code style={styles.keyCode}>{newKeyData.apiKey}</code>
                                    <button
                                        style={styles.copyButton}
                                        onClick={() => copyToClipboard(newKeyData.apiKey)}
                                        title="Copiar al portapapeles"
                                    >
                                        <AiOutlineCopy /> Copiar
                                    </button>
                                </div>

                                <div style={styles.keyDetails}>
                                    <div style={styles.detailItem}>
                                        <AiOutlineCalendar style={styles.detailIcon} />
                                        <span>Expira: {formatDate(newKeyData.expiresAt)}</span>
                                    </div>
                                    <div style={styles.detailItem}>
                                        <AiOutlineClockCircle style={styles.detailIcon} />
                                        <span>Descripción: {newKeyData.description}</span>
                                    </div>
                                </div>

                                <button
                                    style={styles.doneButton}
                                    onClick={() => setShowCreateModal(false)}
                                >
                                    He guardado la clave
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: "40px",
        color: "#374151",
        backgroundColor: "#F9FAFB",
        minHeight: "100%",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
    },
    titleContainer: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    titleIcon: {
        fontSize: "32px",
        color: "#00468B",
    },
    title: {
        fontSize: "28px",
        margin: 0,
        color: "#111827",
    },
    description: {
        fontSize: "16px",
        color: "#6B7280",
        marginBottom: "30px",
        maxWidth: "800px",
        lineHeight: "1.5",
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 16px',
        backgroundColor: '#6B7280',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '600',
        transition: 'all 0.2s ease',
    },
    backIcon: {
        fontSize: '16px',
    },
    addButton: {
        backgroundColor: "#00468B",
        color: "white",
        border: "none",
        padding: "10px 20px",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
    },
    tableCard: {
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        overflow: "hidden",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        textAlign: "left",
    },
    tableHeadRow: {
        backgroundColor: "#F9FAFB",
        borderBottom: "1px solid #E5E7EB",
    },
    th: {
        padding: "12px 16px",
        fontSize: "12px",
        fontWeight: "600",
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    td: {
        padding: "16px",
        borderBottom: "1px solid #E5E7EB",
        verticalAlign: "middle",
        fontSize: "14px",
    },
    keyDescription: {
        fontWeight: "600",
        color: "#111827",
    },
    keyPreview: {
        fontSize: "12px",
        color: "#9CA3AF",
        fontFamily: "monospace",
        marginTop: "4px",
    },
    activeBadge: {
        backgroundColor: "#DEF7EC",
        color: "#03543F",
        padding: "4px 8px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "500",
    },
    revokedBadge: {
        backgroundColor: "#FDE8E8",
        color: "#9B1C1C",
        padding: "4px 8px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "500",
    },
    revokeButton: {
        background: "none",
        border: "none",
        color: "#EF4444",
        cursor: "pointer",
        padding: "8px",
        borderRadius: "4px",
        transition: "background-color 0.2s",
        fontSize: "18px",
    },
    emptyCell: {
        padding: "40px",
        textAlign: "center",
        color: "#9CA3AF",
        fontSize: "16px",
    },
    loading: {
        textAlign: "center",
        padding: "40px",
        fontSize: "18px",
        color: "#6B7280",
    },
    // Modal Styles
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
    },
    modal: {
        backgroundColor: "white",
        borderRadius: "12px",
        width: "90%",
        maxWidth: "600px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    modalHeader: {
        padding: "20px",
        borderBottom: "1px solid #E5E7EB",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    closeButton: {
        background: "none",
        border: "none",
        fontSize: "24px",
        cursor: "pointer",
        color: "#9CA3AF",
    },
    form: {
        padding: "20px",
    },
    formGroup: {
        marginBottom: "20px",
    },
    label: {
        display: "block",
        fontSize: "14px",
        fontWeight: "600",
        color: "#374151",
        marginBottom: "8px",
    },
    input: {
        width: "100%",
        padding: "10px 12px",
        border: "1px solid #D1D5DB",
        borderRadius: "6px",
        fontSize: "14px",
    },
    select: {
        width: "100%",
        padding: "10px 12px",
        border: "1px solid #D1D5DB",
        borderRadius: "6px",
        fontSize: "14px",
        backgroundColor: "white",
    },
    hint: {
        fontSize: "12px",
        color: "#6B7280",
        marginTop: "4px",
        display: "block",
    },
    modalFooter: {
        padding: "20px",
        borderTop: "1px solid #E5E7EB",
        display: "flex",
        justifyContent: "flex-end",
        gap: "12px",
    },
    cancelButton: {
        padding: "10px 16px",
        backgroundColor: "white",
        color: "#374151",
        border: "1px solid #D1D5DB",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
    },
    submitButton: {
        padding: "10px 16px",
        backgroundColor: "#00468B",
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
    },
    successContent: {
        padding: "20px",
        textAlign: "center",
    },
    warningAlert: {
        backgroundColor: "#FFFBEB",
        borderLeft: "4px solid #F59E0B",
        padding: "16px",
        textAlign: "left",
        marginBottom: "20px",
        fontSize: "14px",
        color: "#92400E",
    },
    keyDisplay: {
        display: "flex",
        alignItems: "stretch",
        gap: "0",
        marginBottom: "20px",
        borderRadius: "6px",
        overflow: "hidden",
        border: "1px solid #D1D5DB",
    },
    keyCode: {
        flex: 1,
        padding: "12px",
        backgroundColor: "#F3F4F6",
        textAlign: "left",
        fontFamily: "monospace",
        fontSize: "14px",
        color: "#111827",
        wordBreak: "break-all",
    },
    copyButton: {
        backgroundColor: "#00468B",
        color: "white",
        border: "none",
        padding: "0 16px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontWeight: "600",
    },
    keyDetails: {
        textAlign: "left",
        marginBottom: "20px",
        fontSize: "14px",
        color: "#6B7280",
    },
    detailItem: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
    },
    detailIcon: {
        fontSize: "16px",
    },
    doneButton: {
        width: "100%",
        padding: "12px",
        backgroundColor: "#10B981",
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "16px",
        fontWeight: "600",
        cursor: "pointer",
    }
};

export default MyApiKeys;
